<template>
    <TransitionRoot
        as="template"
        :show="mounted && isModalOpen"
    >
        <Dialog
            as="div"
            class="relative z-10"
            @close="isModalOpen = false"
        >
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-gray-400 bg-opacity-75 transition-opacity"></div>
            </TransitionChild>

            <div
                v-show="isModalOpen"
                class="fixed inset-0 z-10 overflow-y-auto"
            >
                <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            :class="`
                                w-screen md:w-auto inline-block md:mt-36 align-bottom bg-white rounded-xl
                                text-left overflow-hidden shadow transform transition-all md:align-middle
                                md:max-w-screen-lg mx-10
                            `"
                        >
                            <div class="absolute top-2.5 right-2.5 cursor-pointer flex flex-col items-center z-50">
                                <button @click="isModalOpen = false">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                        class="w-6 h-6"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div class="p-6">
                                <DialogTitle class="text-xl font-bold text-center mb-4">
                                    {{ translations.modalTitle }}
                                </DialogTitle>
                                <div>
                                    <div class="mb-2">
                                        <!-- eslint-disable vue/no-v-html -->
                                        <div
                                            class="mb-4"
                                            v-html="translations.modalText"
                                        ></div>
                                    </div>

                                    <div class="mb-2">
                                        {{ translations.modalAlwaysTitle }}
                                    </div>
                                    <SwitchGroup
                                        as="div"
                                        class="flex mb-6"
                                    >
                                        <div class="mr-6">
                                            <Switch
                                                :default-checked="true"
                                                class="bg-base-400 relative inline-flex h-6 w-11 items-center rounded-full"
                                            >
                                                <span
                                                    class="translate-x-6 inline-block h-4 w-4 transform rounded-full bg-white transition"
                                                ></span>
                                            </Switch>
                                        </div>
                                        <div>
                                            <SwitchLabel
                                                class="mr-4"
                                            >
                                                {{ translations.modalAlwaysText }}
                                            </SwitchLabel>
                                        </div>
                                    </SwitchGroup>

                                    <div class="mb-2">
                                        {{ translations.modalAdsTitle }}
                                    </div>
                                    <SwitchGroup
                                        as="div"
                                        class="flex"
                                    >
                                        <div class="mr-6">
                                            <Switch
                                                v-model="checkedCookieConsent"
                                                :class="checkedCookieConsent ? 'bg-base-400' : 'bg-gray-200'"
                                                class="relative inline-flex h-6 w-11 items-center rounded-full"
                                            >
                                                <span
                                                    :class="checkedCookieConsent ? 'translate-x-6' : 'translate-x-1'"
                                                    class="inline-block h-4 w-4 transform rounded-full bg-white transition"
                                                ></span>
                                            </Switch>
                                        </div>
                                        <div class="content">
                                            <SwitchLabel
                                                class="mr-4"
                                            >
                                                {{ translations.modalAdsText }}
                                            </SwitchLabel>

                                            <!-- eslint-disable vue/no-v-html -->
                                            <div
                                                v-show="!checkedCookieConsent"
                                                class="mt-2 ml-6"
                                                v-html="translations.modalAdsTextOff"
                                            ></div>
                                        </div>
                                    </SwitchGroup>
                                </div>
                                <div class="flex justify-center mt-4">
                                    <button
                                        class="flex items-center font-bold !text-white bg-base-400 py-3 px-8 hover:!bg-base-600 hover:!no-underline rounded ease-out duration-300"
                                        @click="save"
                                    >
                                        {{ translations.modalButton }}
                                    </button>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>

    <div
        v-show="mounted"
        class="container"
    >
        <div class="flex justify-center">
            <button
                class="text-sm text-base-400 bg-white py-1 px-10 md:px-20 rounded hover:text-base-600 hover:shadow"
                @click="isModalOpen = true"
            >
                {{ translations.button }}
            </button>
        </div>
    </div>

    <div
        v-show="showCookieWall"
        class="fixed bottom-0 w-full border-0 text-gray-400 bg-white py-4 z-10 shadow-inner"
    >
        <div class="container">
            <div class="flex flex-col md:flex-row justify-between">
                <div class="md:w-3/4 mb-4 md:mb-0">
                    <div class="text-xs md:text-base font-bold mb-2">
                        {{ translations.overlayTitle }}
                    </div>
                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        class="text-xs md:text-sm"
                        v-html="translations.overlayText"
                    ></div>
                </div>

                <div class="flex justify-center items-center md:w-1/4 md:ml-20">
                    <button
                        class="flex items-center font-bold !text-white bg-base-400 py-1 md:py-3 px-2 md:px-4 hover:!bg-base-600 hover:!no-underline rounded ease-out duration-300"
                        @click="save"
                    >
                        {{ translations.overlayButton }}

                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-8 h-8 ml-1 md:ml-4"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, inject, onMounted, computed } from 'vue';
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    SwitchGroup,
    SwitchLabel,
    Switch,
} from '@headlessui/vue';
import { usePage, router } from '@inertiajs/vue3';

const page = usePage();

let $cookies = null;
const mounted = ref(false);
const isModalOpen = ref(false);
const hasCookieConsentKey = ref(false);
const checkedCookieConsent = ref(false);

const translations = page.props.settings.translations.cookieConsent;

onMounted(() => {
    $cookies = inject('$cookies');

    hasCookieConsentKey.value = $cookies.isKey('cookieConsent');
    checkedCookieConsent.value = !hasCookieConsentKey.value || $cookies.get('cookieConsent') === '1';

    router.on('navigate', () => {
        setTimeout(() => {
            hasCookieConsentKey.value = $cookies.isKey('cookieConsent');
            checkedCookieConsent.value = !hasCookieConsentKey.value || $cookies.get('cookieConsent') === '1';
        });
    });

    mounted.value = true;

    setTimeout(() => {
        const togglers = document.querySelectorAll('.cookie-toggler');
        togglers.forEach(toggler => toggler.addEventListener('click', () => isModalOpen.value = true));
    });
});

const showCookieWall = computed(() => !(page.props.hasCookieConsentCookie || hasCookieConsentKey.value));

function gtag(...parameters) {
    window.dataLayer.push(...parameters);
}

function save() {
    isModalOpen.value = false;

    $cookies.set('cookieConsent', checkedCookieConsent.value ? '1' : '0');
    hasCookieConsentKey.value = true;

    gtag('consent', 'update', {
        ad_storage: checkedCookieConsent.value ? 'granted' : 'denied',
        analytics_storage: checkedCookieConsent.value ? 'granted' : 'denied',
    });

    const _paq = window._paq || [];
    if (checkedCookieConsent.value) {
        _paq.push(['rememberConsentGiven']);
    } else {
        _paq.push(['forgetConsentGiven']);
    }
}
</script>
<style scoped>
:deep(a), :deep(.cookie-toggler) {
  @apply cursor-pointer underline text-base-400 hover:text-base-600 ease-out duration-300;
}
</style>
